import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Select, Table, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { EditOutlined, EyeFilled } from "@ant-design/icons";
import "../../css/common.css";
import { post } from "../../../services/services";
import {
  ApiURL,
  oilpartonelogdownloadPdfUrl,
} from "../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../widgets/AppToast";
import VoyageDetails from "../../../components/VoyageDetails";
import { formatDate } from "../../../utils/dateFormatter";
import useVoyageList from "../../../hooks/useVoyageList";
import useUserData from "../../../hooks/useUserData";
import dayjs from "dayjs";
import { dateRanges } from "../../../Data/data";
import { DOWNLOADPDF } from "../../../utils/downloadPdf";
import RequestFormModal from "../../../components/RequestFormModal.js";
import GraphHandler from "../../../components/GraphHandler.js";

const { RangePicker } = DatePicker;

const ListOilRecordLogOne = () => {
  const navigate = useNavigate();
  const user = useUserData();
  const { voyageList } = useVoyageList();
  const [page_loader, set_page_loader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [staff_dashboard, set_staff_dashboard] = useState([]);
  const [voyage_ids, set_voyage_id] = useState("");
  const [voyage_status, set_voyage_status] = useState(true);
  const [selectedChiefRows, setSelectedChiefRows] = useState([]);
  const [selectedMasterRows, setSelectedMasterRows] = useState([]);
  const [isModalVisible, setModalVisible] = useState(false);
  const [record_id, set_record_id] = useState("");
  const [dates, setDates] = useState([dayjs().subtract(2, "day"), dayjs()]);
  const [startDate, endDate] = dates?.map((d) => d.format("YYYY-MM-DD")) || [];

  // /// orb chart example start///
  const [open, setOpen] = useState(false);
  const [selectedChart, setSelectedChart] = useState("bar");
  const [timeFormat, setTimeFormat] = useState("daily");

  /// orb chart example start ///
  const [series, setSeries] = useState([]);
  const [xAxis, setXAxis] = useState([]);
  const [reportDates, setReportDates] = useState([]);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [startDates, setStartDates] = useState(null);
  const [endDates, setEndDates] = useState(null);

  // Transform data by time format
  const transformDataByFormat = (rawXAxis, rawSeries, format) => {
    if (!rawXAxis || !rawSeries) return { xAxis: [], series: [] };
    const parsedDates = rawXAxis[0]?.data?.map((d) => dayjs(d));
    if (!parsedDates) return { xAxis: [], series: [] };

    let groups = {};
    parsedDates.forEach((date, idx) => {
      let key = "";
      if (format === "daily") key = date.format("YYYY-MM-DD");
      if (format === "weekly") key = `${date.year()}-W${date.isoWeek()}`;
      if (format === "monthly") key = date.format("YYYY-MM");
      if (!groups[key]) groups[key] = [];
      groups[key].push(idx);
    });

    const newXAxis = Object.keys(groups);
    const newSeries = rawSeries.map((serie) => {
      const groupedData = newXAxis.map((group) => {
        const indices = groups[group];
        const values = indices.map((i) => serie.data[i] || 0);
        return values.reduce((a, b) => a + b, 0) / values.length;
      });
      return { ...serie, data: groupedData };
    });

    return {
      xAxis: [{ scaleType: "band", data: newXAxis }],
      series: newSeries,
    };
  };

  const { xAxis: transformedXAxis, series: transformedSeries } = useMemo(
    () => transformDataByFormat(xAxis, series, timeFormat),
    [xAxis, series, timeFormat]
  );

  const scatterSeries = useMemo(
    () =>
      transformedSeries?.map((item) => ({
        label: item.label,
        color: item.color,
        data: item?.data?.map((value, index) => ({
          x: index,
          y: value,
        })),
      })),
    [transformedSeries]
  );

  const GET_ROB_GRAPH_DATA = () => {
    if (!startDates || !endDates || selectedParameters.length === 0) return;
    const FORM_DATA = new FormData();
    FORM_DATA.append("start_date", startDates);
    FORM_DATA.append("end_date", endDates);
    FORM_DATA.append("machines", JSON.stringify(selectedParameters));
    post(ApiURL.get_tank_rob_graph_data_st, FORM_DATA)
      .then((res) => {
        if (res?.status === true) {
          setSeries(res.data.series || []);
          setXAxis(res.data.xAxis || []);
        }
      })
      .catch((err) => console.error("Error fetching chart data:", err));
  };

  useEffect(() => {
    const staffData = JSON.parse(
      localStorage.getItem("staff_dashboard") || "{}"
    );
    set_staff_dashboard(staffData);
    localStorage.setItem("viewOnly", 0);
  }, []);

  useEffect(() => {
    if (voyageList?.length > 0) {
      const runningVoyage = voyageList.find(
        (voyage) => voyage.voyage_status === true
      );
      set_voyage_status(runningVoyage?.voyage_status || false);
      if (runningVoyage) {
        set_voyage_id(runningVoyage.id);
        if (runningVoyage?.id) LIST_OIL_RECORD_LOGBOOK_API(runningVoyage.id);
      }
    }
  }, [voyageList]);

  const LIST_OIL_RECORD_LOGBOOK_API = (voyage_id) => {
    set_page_loader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("voyage_id", voyage_id);
      FORM_DATA.append("orb_part", "1");
      post(ApiURL.list_orb_records_st, FORM_DATA).then((res) => {
        if (res?.status == true) {
          const reversedData = res.records?.map((item) => ({
            ...item,
            key: item.record_id.toString(),
            chiefReviewed: item.reviewed_by_chief_engineer || false,
            masterReviewed: item.reviewed_by_master || false,
            i_code:
              item.reviewed_by_chief_engineer &&
              item.reviewed_by_master &&
              item.code_category == "I"
                ? false
                : true,
          }));

          setFilteredData(reversedData);
        }
        set_page_loader(false);
      });
    } catch (error) {
      console.error("Error fetching Staff list:", error);
      set_page_loader(false);
    }
  };

  const CHANGE_CHIEF_MASTER_REVIEW_CHECK_BOX = (record_id, chief_reviewed) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("record_id", record_id);
    FORM_DATA.append("chief_reviewed", chief_reviewed);
    FORM_DATA.append("orb_part", "1");
    post(ApiURL.update_single_checkbox_st, FORM_DATA).then((res) => {
      if (res?.status == true) {
        SuccessToast("Success message !!", res?.message);
        LIST_OIL_RECORD_LOGBOOK_API(voyage_ids);
      } else {
        ErrorToast("Error message", res?.message);
      }
    });
  };

  const CHANGE_ALL_CHIEF_MASTER_REVIEW_CHECK_BOX = (chief_reviewed) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("chief_reviewed", chief_reviewed);
    FORM_DATA.append("orb_part", "1");
    post(ApiURL.update_multiple_checkboxes_st, FORM_DATA).then((res) => {
      if (res?.status == true) {
        SuccessToast("Success message !!", res?.message);
        LIST_OIL_RECORD_LOGBOOK_API(voyage_ids);
      } else {
        ErrorToast("Error message", res?.message);
      }
    });
  };

  const handleChiefCheckboxChange = (e, record, index) => {
    if (user?.data?.designation !== "Chief Engineer") return;
    const checked = e.target.checked;
    const updatedData = [...filteredData];
    updatedData[index].chiefReviewed = checked;
    if (checked) {
      CHANGE_CHIEF_MASTER_REVIEW_CHECK_BOX(record?.record_id, 0);
      setSelectedChiefRows([...selectedChiefRows, record.key]);
    } else {
      CHANGE_CHIEF_MASTER_REVIEW_CHECK_BOX(record?.record_id, 0);
      setSelectedChiefRows(
        selectedChiefRows.filter((key) => key !== record.key)
      );
      updatedData[index].masterReviewed = false;
      setSelectedMasterRows(
        selectedMasterRows.filter((key) => key !== record.key)
      );
    }
    setFilteredData(updatedData);
  };

  const handleMasterCheckboxChange = (e, record, index) => {
    if (user?.data?.designation !== "Captain") return;
    const checked = e.target.checked;
    const updatedData = [...filteredData];
    updatedData[index].masterReviewed = checked;
    if (checked) {
      CHANGE_CHIEF_MASTER_REVIEW_CHECK_BOX(record?.record_id, 1);
      setSelectedMasterRows([...selectedMasterRows, record.key]);
    } else {
      CHANGE_CHIEF_MASTER_REVIEW_CHECK_BOX(record?.record_id, 1);
      setSelectedMasterRows(
        selectedMasterRows.filter((key) => key !== record.key)
      );
    }
    setFilteredData(updatedData);
  };

  const handleSelectAllChief = (e) => {
    if (user?.data?.designation !== "Chief Engineer") return;
    const checked = e.target.checked;
    const updatedData = filteredData.map((item) => ({
      ...item,
      chiefReviewed: checked,
      masterReviewed: checked ? item.masterReviewed : false,
    }));
    setFilteredData(updatedData);
    setSelectedChiefRows(checked ? filteredData.map((item) => item.key) : []);
    CHANGE_ALL_CHIEF_MASTER_REVIEW_CHECK_BOX(0);
    if (!checked) {
      setSelectedMasterRows([]);
    }
  };

  const handleSelectAllMaster = (e) => {
    if (user?.data?.designation !== "Captain") return;
    const checked = e.target.checked;
    const updatedData = filteredData.map((item) => ({
      ...item,
      masterReviewed: item.chiefReviewed && checked,
    }));
    setFilteredData(updatedData);
    setSelectedMasterRows(
      checked && filteredData.every((item) => item.chiefReviewed)
        ? filteredData.map((item) => item.key)
        : []
    );
    CHANGE_ALL_CHIEF_MASTER_REVIEW_CHECK_BOX(1);
  };

  const CREATE_REQUEST_EDIT_VARIFAY = async (formData) => {
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("voyage_id", voyage_ids);
      FORM_DATA.append("orb_part", "part 1");
      FORM_DATA.append("record_id", record_id);
      FORM_DATA.append(
        "description",
        formData?.trim()
          ? formData
          : "I want to update this ORB record because I made some mistakes while entering it."
      );

      const response = await post(ApiURL?.notification_save_req, FORM_DATA);

      if (response?.data?.status === true) {
        SuccessToast("Congratulations!", response?.data?.messages);
      } else {
        ErrorToast("Error", response?.data?.message || "Something went wrong.");
      }

      setModalVisible(false);
    } catch (error) {
      console.error("Error in CREATE_REQUEST_EDIT_VARIFAY:", error);
      ErrorToast(
        "Request Failed",
        "Could not complete the request. Please try again."
      );
    }
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "record_id",
      align: "center",
      key: "record_id",
      sorter: (a, b) => a.record_id - b.record_id,
      render: (text, record) => (
        <span
          style={record?.soft_delete ? { textDecoration: "line-through" } : {}}
        >
          {record?.record_id}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      align: "center",
      key: "date",
      render: (text, record) => (
        <span
          style={record?.soft_delete ? { textDecoration: "line-through" } : {}}
        >
          {record?.date}
        </span>
      ),
    },
    {
      title: "Code Letter",
      dataIndex: "code_category",
      align: "center",
      key: "code_category",
      render: (text, record) => (
        <span
          style={record?.soft_delete ? { textDecoration: "line-through" } : {}}
        >
          {record?.code_category}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      align: "center",
      key: "created_at",
      render: (text, record) => (
        <span
          style={record?.soft_delete ? { textDecoration: "line-through" } : {}}
        >
          {formatDate(record?.created_at)}
        </span>
      ),
    },
    {
      title: "Updated Date",
      dataIndex: "updated_at",
      align: "center",
      key: "updated_at",
      render: (text, record) => (
        <span
          style={record?.soft_delete ? { textDecoration: "line-through" } : {}}
        >
          {formatDate(record?.updated_at)}
        </span>
      ),
    },

    {
      title: (
        <Tooltip
          title={
            selectedChiefRows.length > 0
              ? `Selected: ${selectedChiefRows.join(", ")}`
              : "No selections"
          }
        >
          <div style={{ textAlign: "center" }}>
            <span>Confirmed by Chief Engineer</span>
            <Checkbox
              style={{ marginLeft: 8 }}
              onChange={(e) => handleSelectAllChief(e)}
              checked={
                filteredData.length > 0 &&
                filteredData.every((item) => item.chiefReviewed)
              }
              indeterminate={
                filteredData.some((item) => item.chiefReviewed) &&
                !filteredData.every((item) => item.chiefReviewed)
              }
              disabled
            />
          </div>
        </Tooltip>
      ),
      dataIndex: "updated_at",
      align: "center",
      key: "chief_review",
      render: (text, record, index) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Checkbox
            checked={record.chiefReviewed}
            onChange={(e) => handleChiefCheckboxChange(e, record, index)}
            disabled
          />
          <span
            style={{
              marginLeft: 8,
              textDecoration: record?.soft_delete ? "line-through" : "none",
            }}
          >
            Confirmed by Chief Engineer
          </span>
        </div>
      ),
    },
    {
      title: (
        <Tooltip
          title={
            selectedMasterRows.length > 0
              ? `Selected: ${selectedMasterRows.join(", ")}`
              : "No selections"
          }
        >
          <div style={{ textAlign: "center" }}>
            <span>Reviewed By Master</span>
            <Checkbox
              style={{ marginLeft: 8 }}
              onChange={handleSelectAllMaster}
              checked={
                filteredData.length > 0 &&
                filteredData.every((item) => item.masterReviewed)
              }
              indeterminate={
                filteredData.some((item) => item.masterReviewed) &&
                !filteredData.every((item) => item.masterReviewed)
              }
              disabled
            />
          </div>
        </Tooltip>
      ),
      dataIndex: "updated_at",
      align: "center",
      key: "master_review",
      render: (text, record, index) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Checkbox
            checked={record.masterReviewed}
            onChange={(e) => handleMasterCheckboxChange(e, record, index)}
            disabled
          />
          <span
            style={{
              marginLeft: 8,
              textDecoration: record?.soft_delete ? "line-through" : "none",
            }}
          >
            Reviewed By Master
          </span>
        </div>
      ),
    },
    {
      title: "Action",
      align: "center",
      key: "Action",
      render: (text, record) => (
        <>
          {!record?.record_for_I && (
            <>
              {(() => {
                const isCaptain =
                  user?.data?.designation === "Chief Engineer" ||
                  user?.data?.designation === "Captain";
                const openModle = user?.data?.designation === "Chief Engineer";
                const isSoftDeleted = record?.soft_delete;
                const reviewed = record?.reviewed_by_master;
                const I_CODE = record?.i_code;

                const handleEdit = () => {
                  if (reviewed && isCaptain) {
                    if (openModle) {
                      localStorage.setItem("viewOnly", 1);
                      setModalVisible(!isModalVisible);
                    } else {
                      localStorage.setItem("viewOnly", 1);
                      navigate(
                        `/edit-oil-record-log-one/${btoa(record?.record_id)}`
                      );
                    }
                    set_record_id(record?.record_id);
                  } else {
                    localStorage.setItem("viewOnly", 1);
                    navigate(
                      `/edit-oil-record-log-one/${btoa(record?.record_id)}`
                    );
                  }
                };

                const viewButton = (
                  <Tooltip title="View">
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        navigate(
                          `/view-oil-record-log-one/${btoa(record?.record_id)}`
                        );
                      }}
                      style={{ marginRight: "5px" }}
                      icon={<EyeFilled />}
                    />
                  </Tooltip>
                );

                if (!isSoftDeleted && isCaptain && I_CODE) {
                  return (
                    <>
                      <Tooltip
                        title={
                          !voyage_status
                            ? "Editing is disabled when voyage status is inactive."
                            : "Edit"
                        }
                      >
                        <span>
                          <Button
                            disabled={!voyage_status}
                            type="primary"
                            size="small"
                            onClick={handleEdit}
                            icon={<EditOutlined />}
                          />
                        </span>
                      </Tooltip>

                      <Tooltip title="View">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => {
                            localStorage.setItem("viewOnly", 0);
                            navigate(
                              `/view-oil-record-log-one/${btoa(
                                record?.record_id
                              )}`
                            );
                          }}
                          style={{ marginLeft: "15px" }}
                          icon={<EyeFilled />}
                        />
                      </Tooltip>
                    </>
                  );
                }
                return viewButton;
              })()}
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <VoyageDetails
        staff_dashboard={staff_dashboard?.voyage_details}
        buttonShow={true}
        graphbtn={true}
        disabled={!voyage_status}
        buttonProps={{
          text: "Add Logs",
          className: "save_btn",
          onClick: () => navigate("/add-oil-record-log-one"),
          onPress: () => navigate("/audit-trail-oil-one"),
          onclick: () => setOpen(true),
        }}
      />
      <div className="row">
        <div
          className="col-3"
          style={{ marginBottom: "13px", marginLeft: "15px" }}
        >
          <label className="block-label">Select Voyage Id</label>
          <Select
            placeholder="---- Select Voyage Id ----"
            style={{ width: "100%" }}
            value={voyage_ids}
            onChange={(value) => {
              const selectedVoyage = voyageList.find(
                (voyage) => voyage.id === value
              );
              if (selectedVoyage) {
                set_voyage_status(selectedVoyage.voyage_status);
              }
              set_voyage_id(value);
              LIST_OIL_RECORD_LOGBOOK_API(value);
            }}
          >
            {voyageList?.map((voyage) => (
              <Select.Option key={voyage.id} value={voyage.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span>{`Voyage Id: ${voyage.id}`}</span>
                  <span
                    style={{
                      color:
                        voyage.voyage_status === true ? "#FFA500" : "#008000",
                      fontWeight: "550",
                      marginRight: "15px",
                    }}
                  >
                    {voyage.voyage_status === true ? "Running" : "Complete"}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="col-3"></div>
        <div
          className="col-6"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <div style={{ marginRight: "15px" }}>
            <label className="block-label">Select Start Date & End Date</label>
            <RangePicker
              presets={dateRanges}
              value={dates}
              onChange={setDates}
              format="YYYY-MM-DD"
              style={{ width: 350 }}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </div>
          <div style={{ alignSelf: "flex-end" }}>
            <Button
              type="primary"
              className="save_btn"
              style={{ color: "white", marginBottom: "13px" }}
              onClick={() =>
                DOWNLOADPDF(
                  oilpartonelogdownloadPdfUrl,
                  voyage_ids,
                  startDate,
                  endDate
                )
              }
            >
              Download Pdf
            </Button>
          </div>
        </div>
      </div>
      <Table
        bordered
        columns={columns}
        dataSource={filteredData}
        pagination={{ pageSize: 10 }}
        rowKey="key"
        loading={page_loader}
        className="custom-table"
      />
      <RequestFormModal
        visible={isModalVisible}
        record_id={record_id}
        voyage_ids={voyage_ids}
        setModalVisible={setModalVisible}
        onCancel={() => {
          setModalVisible(!isModalVisible);
        }}
        onSubmit={CREATE_REQUEST_EDIT_VARIFAY}
        designation={user?.data?.designation}
      />
      <>
        <GraphHandler
          open={open}
          onCancel={() => setOpen(false)}
          reportDates={reportDates}
          setReportDates={setReportDates}
          selectedChart={selectedChart}
          setSelectedChart={setSelectedChart}
          selectedParameters={selectedParameters}
          setSelectedParameters={setSelectedParameters}
          startDate={startDates}
          setStartDate={setStartDates}
          endDate={endDates}
          setEndDate={setEndDates}
          fetchGraphData={GET_ROB_GRAPH_DATA}
          timeFormat={timeFormat}
          setTimeFormat={setTimeFormat}
          transformedSeries={transformedSeries}
          transformedXAxis={transformedXAxis}
          scatterSeries={scatterSeries}
        />
      </>
    </>
  );
};

export default ListOilRecordLogOne;
